/**
 * SupplierPostOpportunityPage ✦
 *
 * Under-30s quick-post flow. All fields inline, single Submit.
 * No wizard. Lives at /dashboard/supplier/crew/new.
 *
 * Uses the Crew neon palette so the supplier feels they're posting
 * into a different surface (the crew sub-product) — distinct from
 * the editorial walnut/charcoal of the main supplier studio.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Lightning, Sparkle } from '@phosphor-icons/react';
import CrewVideoUploader from '../../components/crew/CrewVideoUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BG = '#07071A';
const PANEL = 'rgba(255,255,255,0.05)';
const PANEL_BORDER = 'rgba(255,255,255,0.1)';
const PURPLE = '#6C3BFF';
const PINK = '#FF3CAC';
const BLUE = '#33C3FF';
const TEXT = '#FFFFFF';
const TEXT_MUTED = '#B8B8D1';
const FONT_STACK = `'Inter', 'Poppins', system-ui, sans-serif`;

const TAGS = [
  { key: 'urgent', label: 'Urgent' },
  { key: 'today', label: 'Today' },
  { key: 'beginner_friendly', label: 'Beginner friendly' },
  { key: 'trial_shift', label: 'Trial shift' },
  { key: 'ongoing_role', label: 'Ongoing role' },
  { key: 'bring_friend', label: 'Bring a friend' },
];

const labelStyle = {
  display: 'block', fontFamily: FONT_STACK, fontSize: 11, fontWeight: 700,
  letterSpacing: '0.18em', textTransform: 'uppercase', color: TEXT_MUTED, marginBottom: 6,
};
const inputStyle = {
  width: '100%', padding: '13px 14px', background: 'rgba(255,255,255,0.04)',
  border: `1px solid ${PANEL_BORDER}`, borderRadius: 12,
  fontFamily: FONT_STACK, fontSize: 15, color: TEXT, outline: 'none',
};

const SupplierPostOpportunityPage = () => {
  const navigate = useNavigate();
  const [draft, setDraft] = useState({
    title: '',
    description: '',
    photo_url: '',
    video_url: '',
    video_poster_url: '',
    event_date: '',
    start_time: '',
    end_time: '',
    location: '',
    postcode: '',
    pay: '',
    tags: [],
    helping_with: ['', '', ''],
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const toggleTag = (k) => {
    setDraft((d) => ({
      ...d,
      tags: d.tags.includes(k) ? d.tags.filter((t) => t !== k) : [...d.tags, k],
    }));
  };

  const submit = async () => {
    setError(''); setBusy(true);
    try {
      const r = await axios.post(`${API}/crew/opportunities`, {
        ...draft,
        helping_with: draft.helping_with.filter((s) => (s || '').trim()),
      });
      const oppId = r.data?.opportunity?.id;
      navigate(oppId ? `/dashboard/supplier/crew/${oppId}/applicants` : '/dashboard/supplier/crew');
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate('/login?next=/dashboard/supplier/crew/new');
        return;
      }
      setError(err?.response?.data?.detail || 'Could not post opportunity');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="post-opportunity-page" style={{
      minHeight: '100vh', background: BG, color: TEXT, fontFamily: FONT_STACK,
      padding: 'clamp(20px, 5vw, 56px) clamp(16px, 5vw, 32px) 80px',
    }}>
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        background: `radial-gradient(circle at 20% 10%, ${PURPLE}22 0%, transparent 38%), radial-gradient(circle at 80% 80%, ${PINK}1A 0%, transparent 38%)`,
      }} />
      <div style={{ maxWidth: 640, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <button
          onClick={() => navigate('/dashboard/supplier/crew')}
          data-testid="post-back"
          style={{
            background: 'transparent', border: 'none', color: TEXT_MUTED, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: FONT_STACK,
            fontSize: 13, marginBottom: 28,
          }}
        >
          <ArrowLeft size={14} weight="bold" /> Crew dashboard
        </button>

        <p style={{
          margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: '0.32em',
          textTransform: 'uppercase',
          background: `linear-gradient(135deg, ${PINK}, ${BLUE})`,
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          ✦ Post Opportunity
        </p>
        <h1 style={{
          margin: '14px 0 10px', fontWeight: 900, fontSize: 'clamp(28px, 5vw, 42px)',
          letterSpacing: '-0.025em', lineHeight: 1.08, color: TEXT,
        }}>
          Find crew in 30 seconds.
        </h1>
        <p style={{ color: TEXT_MUTED, fontSize: 14.5, lineHeight: 1.6, marginBottom: 28 }}>
          Drop the basics. Add a photo if you have one. Tag the vibe. Done.
        </p>

        <div style={{ display: 'grid', gap: 18 }}>
          <div>
            <label style={labelStyle} htmlFor="po-title">Role title</label>
            <input id="po-title" data-testid="po-title" style={inputStyle}
              placeholder="e.g. Photobooth Assistant"
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          </div>

          <div>
            <label style={labelStyle} htmlFor="po-photo">Photo URL (optional)</label>
            <input id="po-photo" data-testid="po-photo" style={inputStyle}
              placeholder="https://… (paste an image link)"
              value={draft.photo_url}
              onChange={(e) => setDraft({ ...draft, photo_url: e.target.value })} />
          </div>

          <div>
            <label style={labelStyle}>Vibe video (optional)</label>
            <CrewVideoUploader
              kind="opportunity"
              maxSeconds={20}
              value={{ video_url: draft.video_url, poster_url: draft.video_poster_url }}
              onChange={(next) => setDraft((d) => ({
                ...d,
                video_url: next.video_url || '',
                video_poster_url: next.poster_url || '',
              }))}
              testId="po-video"
            />
          </div>

          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
            <div>
              <label style={labelStyle} htmlFor="po-date">Event date</label>
              <input id="po-date" data-testid="po-date" type="date" style={inputStyle}
                value={draft.event_date}
                onChange={(e) => setDraft({ ...draft, event_date: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle} htmlFor="po-start">Start time</label>
              <input id="po-start" data-testid="po-start" style={inputStyle}
                placeholder="6pm"
                value={draft.start_time}
                onChange={(e) => setDraft({ ...draft, start_time: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle} htmlFor="po-end">End time</label>
              <input id="po-end" data-testid="po-end" style={inputStyle}
                placeholder="11pm"
                value={draft.end_time}
                onChange={(e) => setDraft({ ...draft, end_time: e.target.value })} />
            </div>
          </div>

          <div>
            <label style={labelStyle} htmlFor="po-location">Location</label>
            <input id="po-location" data-testid="po-location" style={inputStyle}
              placeholder="Manchester · Old Trafford"
              value={draft.location}
              onChange={(e) => setDraft({ ...draft, location: e.target.value })} />
          </div>

          <div>
            <label style={labelStyle} htmlFor="po-pay">Pay</label>
            <input id="po-pay" data-testid="po-pay" style={inputStyle}
              placeholder="£12/hr or £60 flat"
              value={draft.pay}
              onChange={(e) => setDraft({ ...draft, pay: e.target.value })} />
          </div>

          {/* Helping with — 3 quick chips */}
          <div>
            <label style={labelStyle}>You'll be helping with (up to 3)</label>
            <div style={{ display: 'grid', gap: 8 }}>
              {[0, 1, 2].map((i) => (
                <input
                  key={i}
                  data-testid={`po-helping-${i}`}
                  style={inputStyle}
                  placeholder={i === 0 ? 'e.g. Guest Interaction' : i === 1 ? 'e.g. Running Booth' : 'e.g. Team Setup'}
                  value={draft.helping_with[i] || ''}
                  onChange={(e) => {
                    const next = [...draft.helping_with];
                    next[i] = e.target.value;
                    setDraft({ ...draft, helping_with: next });
                  }}
                />
              ))}
            </div>
          </div>

          {/* Tags */}
          <div>
            <label style={labelStyle}>Tags</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {TAGS.map((t) => {
                const active = draft.tags.includes(t.key);
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => toggleTag(t.key)}
                    data-testid={`po-tag-${t.key}`}
                    style={{
                      padding: '8px 14px', borderRadius: 999, cursor: 'pointer',
                      fontFamily: FONT_STACK, fontSize: 12.5, fontWeight: 700,
                      color: active ? TEXT : TEXT_MUTED,
                      background: active
                        ? `linear-gradient(135deg, ${PURPLE}, ${PINK})`
                        : 'transparent',
                      border: active ? 'none' : `1px solid ${PANEL_BORDER}`,
                    }}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Description */}
          <div>
            <label style={labelStyle} htmlFor="po-description">Quick description (optional)</label>
            <textarea id="po-description" data-testid="po-description" rows={3}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: FONT_STACK, lineHeight: 1.5 }}
              placeholder="What should the crew know before they show up?"
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          </div>

          {error && (
            <p data-testid="po-error" style={{ color: PINK, fontSize: 13 }}>{error}</p>
          )}

          <button
            onClick={submit}
            disabled={busy}
            data-testid="po-submit"
            style={{
              marginTop: 8, padding: '16px 28px', borderRadius: 999, border: 'none',
              cursor: busy ? 'wait' : 'pointer', color: TEXT, fontFamily: FONT_STACK,
              fontSize: 15, fontWeight: 900, opacity: busy ? 0.7 : 1,
              background: `linear-gradient(135deg, ${PURPLE}, ${PINK})`,
              boxShadow: `0 10px 28px ${PURPLE}55`,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            <Lightning size={16} weight="fill" />
            {busy ? 'Posting…' : 'Post opportunity ✦'}
          </button>

          <p style={{
            marginTop: 4, color: TEXT_MUTED, fontSize: 12,
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            <Sparkle size={11} weight="fill" color={BLUE} />
            Opportunity goes live the second you tap.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SupplierPostOpportunityPage;
